import React, { useEffect, useReducer } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { articlesAPI } from "../services/api";
import { CATEGORIES, CATEGORY_MAP } from "../lib/categories";
import ArticleCard from "../components/ArticleCard";
import Seo from "../components/Seo";
import "./Categories.css";

const initialState = { counts: {}, articles: [], loading: false };
function reducer(state, action) {
  switch (action.type) {
    case "loading": return { ...state, loading: action.value };
    case "counts": return { ...state, counts: action.counts };
    case "articles": return { ...state, articles: action.articles, loading: false };
    default: return state;
  }
}

export default function Categories() {
  const { slug } = useParams();
  const [state, dispatch] = useReducer(reducer, initialState);
  const { counts, articles, loading } = state;

  // Fetch counts for index view
  useEffect(() => {
    if (slug) return;
    let cancelled = false;
    (async () => {
      const promises = CATEGORIES.map((c) =>
        articlesAPI.list({ category: c.slug, page_size: 1 }).then((r) => [c.slug, r.data.total])
      );
      const pairs = await Promise.all(promises);
      if (!cancelled) dispatch({ type: "counts", counts: Object.fromEntries(pairs) });
    })();
    return () => { cancelled = true; };
  }, [slug]);

  // Fetch articles for category view
  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    dispatch({ type: "loading", value: true });
    articlesAPI
      .list({ category: slug, page_size: 24 })
      .then((r) => { if (!cancelled) dispatch({ type: "articles", articles: r.data.items || [] }); })
      .catch(() => { if (!cancelled) dispatch({ type: "articles", articles: [] }); });
    return () => { cancelled = true; };
  }, [slug]);

  // Per-category view
  if (slug) {
    const cat = CATEGORIES.find((c) => c.slug === slug);
    if (!cat) {
      return (
        <div className="categories-page">
          <Seo title="Category not found" noIndex />
          <div className="container" style={{ padding: "120px 0", textAlign: "center" }}>
            <h1 className="heading-2">Category not found</h1>
            <Link to="/categories" className="btn-primary" style={{ marginTop: 24 }}>All categories</Link>
          </div>
        </div>
      );
    }
    return (
      <div className="categories-page" data-testid="category-page">
        <Seo
          title={`${cat.name} News`}
          description={`Latest ${cat.name} news, race reports, analysis and opinion from Race377.`}
        />
        <section className="categories-hero">
          <div className="container">
            <span className="caption">Race377 / Categories</span>
            <h1 className="brand-display" style={{ marginTop: 12 }}>{cat.name}</h1>
            <p className="body-large" style={{ color: "var(--text-secondary)", marginTop: 16, fontSize: "1.1rem", maxWidth: 640, fontWeight: 400 }}>
              All Race377 coverage on {cat.name}.
            </p>
          </div>
        </section>
        <section className="container" style={{ paddingTop: 48 }}>
          {loading ? (
            <div className="loading-container"><div className="loading-spinner" /></div>
          ) : articles.length === 0 ? (
            <p className="body-medium" style={{ padding: "60px 0", color: "var(--text-secondary)" }}>
              No {cat.name} articles yet — check back soon.
            </p>
          ) : (
            <div className="categories-grid">
              {articles.map((a) => <ArticleCard key={a.id} article={a} />)}
            </div>
          )}
        </section>
      </div>
    );
  }

  // Index view
  return (
    <div className="categories-page" data-testid="categories-index-page">
      <Seo
        title="Categories"
        description="Browse Race377 motorsport coverage by series: Formula 1, WEC, IMSA, GT World Challenge, Formula E, IndyCar, NASCAR, Motorsport Business and Opinion."
      />
      <section className="categories-hero">
        <div className="container">
          <span className="caption">Race377</span>
          <h1 className="brand-display" style={{ marginTop: 12 }}>Categories</h1>
          <p className="body-large" style={{ color: "var(--text-secondary)", marginTop: 16, fontSize: "1.1rem", maxWidth: 640, fontWeight: 400 }}>
            Coverage organised by championship. Pick your series.
          </p>
        </div>
      </section>
      <section className="container" style={{ paddingTop: 48 }}>
        <div className="categories-list">
          {CATEGORIES.map((c) => (
            <Link to={`/categories/${c.slug}`} key={c.slug} className="category-row" data-testid={`category-row-${c.slug}`}>
              <div>
                <h3 className="category-row__name">{CATEGORY_MAP[c.slug]}</h3>
                <span className="caption category-row__count">
                  {counts[c.slug] !== undefined ? `${counts[c.slug]} ${counts[c.slug] === 1 ? "article" : "articles"}` : " "}
                </span>
              </div>
              <ArrowRight size={20} />
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
